import { IonModal } from "@ionic/react";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { jsPDF } from "jspdf";
import { useConfig } from "../../contexts/ConfigContext";
import ResonanceLoader from "../../components/atoms/ResonanceLoader/ResonanceLoader";
import DisplayContent from "../../components/molecules/DisplayContent";
import { getStudyContent } from "../../services/study.service";
import { StudyContentType } from "../../types/welcome";
import { toPdfParagraphs } from "../../helper/richContentText";
import {
  ModalOverlay,
  ModalCard,
  ModalTitle,
  ModalDescription,
  ModalActions,
  ModalOutlineButton,
  ModalQuietButton,
} from "../../components/atoms/ModalKit";

interface Props {
  isOpen: boolean;
  onDismiss(): void;
}

// PDF text comes from the shared walker (helper/richContentText.ts) — the same
// extraction that drives TTS, so PDF and vocalisation can never drift apart.

const LegalMentionModal: FC<Props> = ({ isOpen, onDismiss }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { study, selectedRole } = useConfig();

  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [contentData, setContentData] = useState<StudyContentType | null>(null);

  useEffect(() => {
    let isCancelled = false;

    const loadConsentContent = async () => {
      if (!isOpen) return;

      const studyId = study?.id;
      const consentId = selectedRole?.consent?.id;

      if (!studyId || !consentId) {
        setContentData(null);
        setIsError(true);
        return;
      }

      setIsLoading(true);
      setIsError(false);

      try {
        const { data } = await getStudyContent({
          studyId,
          contentId: consentId,
          lang: language,
        });

        if (!isCancelled) {
          setContentData(data);
        }
      } catch (_error) {
        if (!isCancelled) {
          setContentData(null);
          setIsError(true);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    loadConsentContent();

    return () => {
      isCancelled = true;
    };
  }, [isOpen, language, selectedRole?.consent?.id, study?.id]);

  const handleDownloadPdf = () => {
    if (!contentData?.content || isDownloadingPdf) return;

    const paragraphs = toPdfParagraphs(contentData.content);
    if (!paragraphs.length) return;

    setIsDownloadingPdf(true);

    try {
      const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
      const margin = 40;
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const maxWidth = pageWidth - margin * 2;
      const lineHeight = 16;
      let y = margin;

      const title = t("profile_information_note");
      doc.setFontSize(16);
      const titleLines = doc.splitTextToSize(title, maxWidth);
      titleLines.forEach((line: string) => {
        if (y > pageHeight - margin) {
          doc.addPage();
          y = margin;
        }
        doc.text(line, margin, y);
        y += lineHeight;
      });

      y += 8;
      doc.setFontSize(11);

      paragraphs.forEach((paragraph) => {
        const lines = doc.splitTextToSize(paragraph, maxWidth);
        lines.forEach((line: string) => {
          if (y > pageHeight - margin) {
            doc.addPage();
            y = margin;
          }
          doc.text(line, margin, y);
          y += lineHeight;
        });
        y += 8;
      });

      const noteTitle = t("profile_information_note") || "information-note";
      const safeBaseName = noteTitle
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-zA-Z0-9-_ ]/g, "")
        .trim()
        .replace(/\s+/g, "-")
        .toLowerCase();
      const datePart = new Date().toISOString().slice(0, 10);

      doc.save(`${safeBaseName || "information-note"}-${datePart}.pdf`);
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  return (
    <IonModal
      className="resonance-modal"
      isOpen={isOpen}
      backdropDismiss={true}
      onWillDismiss={onDismiss}
    >
      <ModalOverlay onClick={onDismiss}>
        <WideModalCard onClick={(e) => e.stopPropagation()}>
          <ModalTitle>{t("profile_information_note")}</ModalTitle>

          <ContentArea>
            {isLoading && <ResonanceLoader />}
            {!isLoading && isError && (
              <ModalDescription>
                {t("common_server_error_desc")}
              </ModalDescription>
            )}
            {!isLoading && !isError && contentData?.content && (
              <DisplayContent editorJsData={contentData.content} />
            )}
            {!isLoading && !isError && !contentData?.content && (
              <ModalDescription>{t("home_content_none_text")}</ModalDescription>
            )}
          </ContentArea>

          <ModalActions>
            <ModalOutlineButton
              type="button"
              onClick={handleDownloadPdf}
              disabled={
                isLoading ||
                isError ||
                !contentData?.content ||
                isDownloadingPdf
              }
            >
              {isDownloadingPdf
                ? t("profile_download_pdf_loading")
                : t("profile_download_pdf")}
            </ModalOutlineButton>
            <ModalQuietButton type="button" onClick={onDismiss}>
              {t("common_close")}
            </ModalQuietButton>
          </ModalActions>
        </WideModalCard>
      </ModalOverlay>
    </IonModal>
  );
};

/** Wide reading panel: keeps the kit card language but preserves this modal's
 *  deliberate 900px width and column layout so ContentArea owns the scroll. */
const WideModalCard = styled(ModalCard)`
  max-width: 900px;
  display: flex;
  flex-direction: column;
  overflow-y: hidden;
`;

const ContentArea = styled.div`
  overflow-y: auto;
  flex: 1;
`;

export default LegalMentionModal;
